import React, { useEffect, useRef, useState, useCallback } from "react";
import Globe from "react-globe.gl";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Users, Calendar, Utensils } from "lucide-react";
import { OUTBREAKS } from "../../data/outbreaks";

export const OutbreakGlobe = () => {
  const globeEl = useRef();
  const wrapRef = useRef();
  const [outbreaks] = useState(OUTBREAKS);
  const [selected, setSelected] = useState(null);
  const [dims, setDims] = useState({ w: 600, h: 600 });

  useEffect(() => {
    const measure = () => {
      if (!wrapRef.current) return;
      const w = wrapRef.current.clientWidth;
      setDims({ w, h: Math.min(Math.max(w, 380), 640) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    if (!globeEl.current) return;
    const controls = globeEl.current.controls();
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.6;
    controls.enableZoom = true;
    controls.minDistance = 180;
    controls.maxDistance = 600;
    globeEl.current.pointOfView({ lat: 20, lng: -60, altitude: 2.5 }, 0);
  }, [dims, outbreaks.length]);

  const flyTo = useCallback((o) => {
    setSelected(o);
    if (globeEl.current) {
      globeEl.current.controls().autoRotate = false;
      globeEl.current.pointOfView({ lat: o.lat, lng: o.lng, altitude: 1.6 }, 1200);
    }
  }, []);

  const maxCases = Math.max(1, ...outbreaks.map((o) => o.cases));

  return (
    <section id="globe" className="relative px-4 py-16 md:py-24" data-testid="globe-section">
      <div className="max-w-6xl mx-auto text-center mb-8">
        <h2 className="stencil text-4xl md:text-6xl" data-testid="globe-title">GLOBAL OUTBREAK TRACKER</h2>
      </div>

      <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-4 items-start">
        {/* globe */}
        <div ref={wrapRef} className="lg:col-span-2 petri p-2 relative overflow-hidden" data-testid="globe-canvas-wrap">
          <div className="absolute top-3 left-3 z-10 bg-[#0a133f] border-2 border-[#b026ff] px-3 py-1">
            <span className="font-mono2 text-[10px] text-[#d9b3ff] uppercase tracking-widest blink">● LIVE FECAL FEED</span>
          </div>
          <Globe
            ref={globeEl}
            width={dims.w - 16}
            height={dims.h}
            backgroundColor="rgba(10,19,63,0)"
            globeImageUrl="//unpkg.com/three-globe/example/img/earth-dark.jpg"
            bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
            atmosphereColor="#b026ff"
            atmosphereAltitude={0.28}
            pointsData={outbreaks}
            pointLat="lat"
            pointLng="lng"
            pointColor={() => "#b026ff"}
            pointAltitude={(d) => 0.06 + (d.cases / maxCases) * 0.5}
            pointRadius={(d) => 0.28 + (d.cases / maxCases) * 0.5}
            pointLabel={(d) => `<div style="font-family:'Space Mono',monospace;background:#0a133f;color:#d9b3ff;border:2px solid #b026ff;padding:6px 10px;font-size:12px;max-width:220px"><b>${d.location}</b><br/>${d.year} · ${d.cases.toLocaleString()} cases<br/><span style="color:#fff">${d.vehicle}</span></div>`}
            onPointClick={flyTo}
            ringsData={outbreaks}
            ringLat="lat"
            ringLng="lng"
            ringColor={() => (t) => `rgba(217,179,255,${1 - t})`}
            ringMaxRadius={(d) => 2 + (d.cases / maxCases) * 4}
            ringPropagationSpeed={2}
            ringRepeatPeriod={1400}
          />
        </div>

        {/* outbreak list */}
        <div className="petri p-4 max-h-[640px] overflow-y-auto" data-testid="globe-outbreak-list">
          <h3 className="stencil text-xl mb-3">CASE FILES</h3>
          <div className="flex flex-col gap-2">
            {outbreaks.map((o) => (
              <button
                key={o.id}
                onClick={() => flyTo(o)}
                data-testid={`outbreak-item-${o.id}`}
                className={`text-left border-2 px-3 py-2 transition-colors hover-shake ${
                  selected?.id === o.id ? "border-[#d9b3ff] bg-[#b026ff]/20" : "border-[#b026ff]/40 bg-[#0a133f]/60"
                }`}
              >
                <div className="flex justify-between items-center gap-2">
                  <span className="font-mono2 text-xs font-bold text-white">{o.location}</span>
                  <span className="font-mono2 text-[10px] text-[#d9b3ff]">{o.year}</span>
                </div>
                <span className="font-mono2 text-[10px] text-[#b026ff]">{o.cases.toLocaleString()} cases · {o.vehicle}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* selected popup */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.9 }}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 petri p-5 max-w-md w-[92%]"
            data-testid="globe-popup"
          >
            <button onClick={() => setSelected(null)} className="absolute top-2 right-2 text-white/70 hover:text-[#b026ff]" data-testid="globe-popup-close">
              <X size={18} />
            </button>
            <h3 className="stencil-pink text-2xl mb-2 pr-6">{selected.location}</h3>
            <div className="flex flex-wrap gap-3 mb-3 font-mono2 text-[11px]">
              <span className="flex items-center gap-1 text-[#d9b3ff]"><Calendar size={13} /> {selected.year}</span>
              <span className="flex items-center gap-1 text-[#b026ff]"><Users size={13} /> {selected.cases.toLocaleString()} cases</span>
              <span className="flex items-center gap-1 text-white/80"><Utensils size={13} /> {selected.vehicle}</span>
              <span className="flex items-center gap-1 text-white/60"><MapPin size={13} /> {selected.country}</span>
            </div>
            <p className="font-mono2 text-sm text-white/85 leading-relaxed">{selected.blurb}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default OutbreakGlobe;
